import { Heart, ImageOff, Instagram, Loader2, Trash2, X, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useSignedImage } from "@/hooks/useSignedImage";
import { cn } from "@/lib/utils";
import type { MyFeaturedRow } from "@/hooks/useMyFeaturedSubmissions";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

const sourceLabel: Record<MyFeaturedRow["submission_source"], string> = {
  credits: "Custom · 5 cr",
  custom_pass: "Custom · Pass",
  featured_pass: "Streak pass",
  tryon: "Try-on",
};

const statusStyle: Record<MyFeaturedRow["status"], string> = {
  pending: "bg-warning/90 text-warning-foreground",
  approved: "bg-success/90 text-success-foreground",
  rejected: "bg-destructive/90 text-destructive-foreground",
};

const statusLabel: Record<MyFeaturedRow["status"], string> = {
  pending: "In review",
  approved: "Live",
  rejected: "Rejected",
};

export const FeaturedHistoryTile = ({
  row,
  onDelete,
  onShowReason,
}: {
  row: MyFeaturedRow;
  onDelete: (id: string) => void;
  onShowReason: (row: MyFeaturedRow) => void;
}) => {
  const navigate = useNavigate();
  const bucket =
    row.status === "approved" && row.permanent_image_path ? "featured-images" : "tryon-results";
  const path = row.status === "approved" ? row.permanent_image_path : row.source_image_path;
  const src = useSignedImage(bucket, path);
  const fullSrc = useSignedImage(bucket, path, { width: 1080, quality: 85, resize: "contain" });
  const isApproved = row.status === "approved" && !!row.permanent_image_path;
  const [open, setOpen] = useState(false);

  const openPreview = () => {
    if (!isApproved) return;
    setOpen(true);
  };

  const openInFeed = () => {
    setOpen(false);
    navigate("/app/aura", { state: { focusFeaturedId: row.id } });
  };

  return (
    <>
      <div className="group relative aspect-[3/4] overflow-hidden rounded-2xl bg-muted shadow-soft ring-1 ring-primary/20">
        {src ? (
          <img src={src} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />

        ) : (
          <div className="flex h-full w-full items-center justify-center text-muted-foreground">
            {row.status === "approved" && !row.permanent_image_path ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <ImageOff className="h-6 w-6" />
            )}
          </div>
        )}

        {isApproved && (
          <button
            type="button"
            onClick={openPreview}
            aria-label="Preview featured submission"
            className="absolute inset-0 z-0 cursor-pointer"
          />
        )}

        <button
          type="button"
          onClick={() => row.status === "rejected" && row.reject_reason && onShowReason(row)}
          className={cn(
            "absolute left-2 top-2 z-10 rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider backdrop-blur",
            statusStyle[row.status],
            row.status === "rejected" && row.reject_reason ? "cursor-pointer" : "cursor-default",
          )}
        >
          {statusLabel[row.status]}
        </button>

        <span className="absolute right-2 top-2 z-10 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-white backdrop-blur">
          {sourceLabel[row.submission_source]}
        </span>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/75 to-transparent p-2">
          <div className="flex items-center justify-between text-xs text-white">
            <span className="flex items-center gap-1 font-medium">
              <Heart className="h-3 w-3" /> {row.like_count}
            </span>
            {row.show_instagram && row.instagram_handle && (
              <span className="flex items-center gap-1 text-white/90">
                <Instagram className="h-3 w-3" />
                <span className="max-w-[80px] truncate">@{row.instagram_handle}</span>
              </span>
            )}
          </div>
          {row.caption && (
            <p className="mt-1 line-clamp-1 text-[11px] text-white/85">{row.caption}</p>
          )}
        </div>

        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onDelete(row.id);
          }}
          aria-label="Delete featured submission"
          className="absolute left-2 bottom-2 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur transition-colors hover:bg-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-md p-0 gap-0 overflow-hidden bg-background border-white/10 [&>button]:hidden"
          aria-describedby={undefined}
        >
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Close"
            className="absolute right-3 top-3 z-20 flex h-9 w-9 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur active:scale-95"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="relative aspect-[3/4] w-full bg-black">
            {fullSrc ? (
              <img src={fullSrc} alt={row.caption ?? "Featured look"} className="h-full w-full object-contain" />
            ) : (
              <div className="h-full w-full animate-pulse bg-muted" />
            )}
          </div>
          <div className="space-y-3 p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <span className="flex items-center gap-1.5 rounded-full bg-red-500/15 px-3 py-1 text-sm font-bold text-red-500 ring-1 ring-red-500/30">
                  <Heart className="h-4 w-4 fill-red-500 text-red-500" /> {row.like_count}
                  <span className="text-[11px] font-medium opacity-80">likes</span>
                </span>
                {row.show_instagram && row.instagram_handle && (
                  <span className="flex items-center gap-1 rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold">
                    <Instagram className="h-3 w-3" /> @{row.instagram_handle}
                  </span>
                )}
              </div>
              <span className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider",
                statusStyle[row.status],
              )}>
                {statusLabel[row.status]}
              </span>
            </div>
            {row.template_label && (
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                {row.template_label}
              </p>
            )}
            {row.caption && (
              <p className="text-sm leading-snug text-foreground/90">"{row.caption}"</p>
            )}
            <p className="text-[11px] text-muted-foreground">
              Approved looks rank by likes — share to earn more.
            </p>
            <Button onClick={openInFeed} className="w-full rounded-full">
              <ExternalLink className="mr-2 h-4 w-4" /> Open in Feed
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
